/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Box } from "@mui/material";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import Table from "components/Table";
import usePaginationHook from "hooks/usePagination";
import { useSnackbar } from "hooks/useSnackbar";
import { setNumberFormat, sortArray } from "utils/miscUtils";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { getAllUsers } from "services/miscService";
import { getAllUsersTableCols } from "utils/tableHelper/extraTableHelper";
import InfoIcon from "@mui/icons-material/Info";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import { SimpleSelectInput } from "components/FormSelectInput";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import SearchIcon from "@mui/icons-material/Search";
import { SimpleTextInput } from "components/FormTextInput";

const SearchByOptions = [
  {
    id: "OPERATOR_ID",
    label: "OPERATOR ID",
  },
  {
    id: "AUTO_TEAM_NAME",
    label: "AUTO TEAM NAME",
  },
  {
    id: "RGS_PLAYER_ID",
    label: "RGS PLAYER ID",
  },
  {
    id: "USER_ID",
    label: "USER ID",
  },
  {
    id: "COUNTRY_CODE",
    label: "COUNTRY CODE",
  },
];

const AllUsers = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [listData, setListData] = React.useState([]);
  const [columns, setColumns] = React.useState([]);
  const [orderDirection, setOrderDirection] = React.useState("asc");
  const [searchBy, setSearchBy] = React.useState(null);
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async ({
    search = null,
    searchBy = null,
    ...rest
  }: {
    search?: any;
    searchBy?: any;
    pageNo?: number;
    pageSize?: number;
  } = {}) => {
    try {
      setLoading(true);
      const payload = {
        pageNo: rest.pageNo ?? pageNo,
        pageSize: rest.pageSize ?? pageSize,
      };

      if (search !== null || search !== "") payload["search"] = search;
      if (searchBy !== null || searchBy !== "") payload["searchBy"] = searchBy;
      const response = await getAllUsers(payload);
      setListData(
        (response.data?.items || []).map((item) => {
          const notValidInputs = [null, undefined];
          item["balance"] = setNumberFormat(item?.balance || 0, 4);
          item["isFtuText"] = !notValidInputs.includes(item.isFTU)
            ? item.isFTU
              ? "Yes"
              : "No"
            : "N/A";
          item["showEosText"] = !notValidInputs.includes(item.showEos)
            ? item.showEos
              ? "Yes"
              : "No"
            : "N/A";
          return item;
        })
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error.message || "Failed fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  const onSearchClick = async () => {
    if (searchValue.length > 0 && searchBy) {
      onChangePage(defaultPageNo);
      await fetchData({ pageNo: defaultPageNo, search: searchValue, searchBy });
    } else
      showSnackbar(
        "Please select search by then enter a search value",
        "error"
      );
  };

  const handleInfoClick = (data: any) => {
    navigate(configData.routes.allUsers.userInfo + `?id=${data?._id}`);
  };

  const reset = () => {
    setSearchBy(null);
    _onSearch("");

    if (pageNo !== defaultPageNo || pageSize !== defaultPageSize) {
      onChangePage(defaultPageNo);
      onPageSizeChange(defaultPageSize);
    } else {
      fetchData({
        pageNo: defaultPageNo,
        pageSize: defaultPageSize,
        search: "",
        searchBy: null,
      });
    }
  };

  React.useEffect(() => {
    setColumns(getAllUsersTableCols());
    fetchData({ search: searchValue, searchBy });
  }, [pageNo, pageSize]);

  return (
    <ContentShell
      title="All Users"
      loading={loading}
      actions={
        <Box style={{ display: "flex", gap: 2, maxHeight: 50 }}>
          <Box style={{ width: "180px" }}>
            <SimpleSelectInput
              name="searchBy"
              placeholdertext="Search By"
              options={SearchByOptions}
              value={searchBy || ""}
              onChange={(e) => setSearchBy(e?.target.value)}
            />
          </Box>
          <Box style={{ width: "180px" }}>
            <SimpleTextInput
              value={searchValue}
              name="searchValue"
              placeholdertext="Search"
              onChange={(e) => _onSearch(e.target.value)}
            />
          </Box>
          <CustomButton startIcon={<SearchIcon />} onClick={onSearchClick}>
            Search
          </CustomButton>
          <CustomButton startIcon={<RestartAltIcon />} onClick={reset}>
            Reset
          </CustomButton>
        </Box>
      }
    >
      <Box>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={({ data }) => (
            <CustomButton
              startIcon={<InfoIcon />}
              onClick={() => handleInfoClick(data)}
            >
              Details
            </CustomButton>
          )}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{}}
        />
      </Box>
    </ContentShell>
  );
};

export default AllUsers;
